import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormField, form } from '@angular/forms/signals';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { ManageBriefTabsMonitorService } from '../../services/manage-brief-tabs-monitor/manage-brief-tabs-monitor';
import { ImageService } from '@/app/services/image/image.service';
import { SocialMonitoring } from '@/app/services/social-monitoring/social-monitoring';
import { VideoPreview } from '../../atoms/video-preview/video-preview';
import { FormatService } from '@/app/common/format/format.service';
import { CarouselPreview } from '../../atoms/carousel-preview/carousel-preview';
import { contentStatus } from './content-status';

@Component({
  selector: 'mbtm-mon-content',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormField, VideoPreview, CarouselPreview, InfiniteScrollDirective],
  templateUrl: './mon-content.html',
  styleUrl: './mon-content.scss',
})
export class MonContent implements OnInit, OnDestroy {

  readonly pageSize = 10;

  private subscriptionList = new Subscription();
  private searchQuerySubject = new Subject<string>();
  searchQueryModel = signal({
    query: '',
  })
  searchQueryForm = form(this.searchQueryModel)

  pageNumber = signal<number>(0);
  placeholderImage = signal<string>('../../../../../assets/images/placeholder.jpeg');
  publishedContent = signal<any[] | undefined>(undefined);
  hasMore = signal<boolean>(true);
  isLoadingMore = signal<boolean>(false);
  loadingPosts = signal<boolean>(true);

  readonly status = computed(() =>
    contentStatus(this.loadingPosts(), this.searchQueryModel().query, this.publishedContent()?.length ?? 0),
  );

  /** Services */
  public manageBriefTabsMonitorService = inject(ManageBriefTabsMonitorService);
  public socialMonitoringService = inject(SocialMonitoring);
  public imageService = inject(ImageService);
  public formatService = inject(FormatService);
  /** Services - END */

  constructor() {
    this.subscriptionList.add(
      this.searchQuerySubject.pipe(debounceTime(350)).subscribe(query => {
        this.getPublishedContent(query);
      })
    );

    effect(() => {
      const query = this.searchQueryModel().query;
      this.searchQuerySubject.next(query);
    });
  }

  ngOnInit(): void {
    if (this.manageBriefTabsMonitorService.focussedMonitoringBrief()) {
      this.getPublishedContent();
    }
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  getPublishedContent(query?: string, append = false): void {
    const brief = this.manageBriefTabsMonitorService.focussedMonitoringBrief();
    if (!brief) {
      this.loadingPosts.set(false);
      return;
    }

    const searchQuery = query ?? this.searchQueryModel().query;

    if (append) {
      if (this.isLoadingMore() || !this.hasMore()) {
        return;
      }
      this.isLoadingMore.set(true);
    } else {
      this.pageNumber.set(0);
      this.hasMore.set(true);
      this.isLoadingMore.set(false);
      this.loadingPosts.set(true);
    }

    this.subscriptionList.add(
      this.socialMonitoringService.getPublishedContent({
        briefId: brief.id,
        take: this.pageSize,
        skip: this.pageNumber() * this.pageSize,
        query: searchQuery || undefined,
      }).subscribe({
        next: (content: any[]) => {
          const chunk = content ?? [];

          if (append && this.publishedContent()) {
            const existing = this.publishedContent()!;
            this.publishedContent.set([
              ...existing,
              ...chunk.filter((item) => !existing.some((existingItem) => existingItem.id === item.id)),
            ]);
          } else {
            this.publishedContent.set(chunk);
          }

          this.hasMore.set(chunk.length >= this.pageSize);
          this.isLoadingMore.set(false);
          this.loadingPosts.set(false);
        },
        error: () => {
          this.isLoadingMore.set(false);
          this.loadingPosts.set(false);
          if (append) {
            this.pageNumber.update((page) => Math.max(0, page - 1));
          } else {
            this.publishedContent.set([]);
            this.hasMore.set(false);
          }
        },
      }),
    );
  }

  onScrollDown(): void {
    if (this.isLoadingMore() || !this.hasMore()) {
      return;
    }

    this.pageNumber.update((page) => page + 1);
    this.getPublishedContent(undefined, true);
  }

  clearSearch(): void {
    this.searchQueryModel.set({ query: '' });
  }

  getHandle(content: any) {
    if (content.socialPlatform == 'TikTok') {
      return `@${content.Handles[0].tiktok}`;
    } else if (content.socialPlatform == 'Instagram') {
      return `@${content.Handles[0].instagram}`;
    } else {
      return 'Unknown';
    }
  }
}
